import _ from "lodash";
import {useState, useMemo} from "react";
import {Line, YAxis} from "recharts";
import {Autocomplete, Stack, TextField} from "@mui/material";
import {BaseChart, ChartComponent} from "./BaseChart";
import {ADMIN_CHART_PROP_NAMES, USER_CHART_PROP_NAMES} from "../../../utils/chartUtils";
import {FieldResult, FieldResultAggregatedProps} from "../../../types/domain/fieldResult";
import {TimeSeriesDataProps} from "../../../types/timeSeriesDataProps";
import dayjs from "dayjs";
import {UserGlobalRole} from "../../../types/domain/user";
import {isUserHaveAnyOfGlobalRoles} from "../../../utils/accessControlUtils";
import {authenticatedUserLoadable} from "../../../store/authenticatedUserStore";
import {useAtomValue} from "jotai";
import {isResourceSucceeded} from "../../../utils/resourceUtils";

export const CustomChart: ChartComponent = ({data}) => {
    const [leftProperties, setLeftProperties] = useState<(keyof FieldResultAggregatedProps)[]>([]);
    const [rightProperties, setRightProperties] = useState<(keyof FieldResultAggregatedProps)[]>([]);
    const chartData = useMemo(() => data.map(mapFieldResultsToChartData), [data]);

    const authenticatedUserResource = useAtomValue(authenticatedUserLoadable);
    const isAdmin =
        isResourceSucceeded(authenticatedUserResource) &&
        isUserHaveAnyOfGlobalRoles(authenticatedUserResource.data, [UserGlobalRole.ADMIN, UserGlobalRole.CS]);

    return (
        <BaseChart
            title="Custom Chart"
            yLabel=""
            data={chartData}
            content={
                <Stack direction="column" gap="0.5rem">
                    <PropertySearch
                        properties={leftProperties}
                        onChange={setLeftProperties}
                        placeholder="Add Property to Left Axis"
                        options={isAdmin ? ADMIN_CHART_PROP_NAMES : USER_CHART_PROP_NAMES}
                    />
                    <PropertySearch
                        properties={rightProperties}
                        onChange={setRightProperties}
                        placeholder="Add Property to Right Axis"
                        options={isAdmin ? ADMIN_CHART_PROP_NAMES : USER_CHART_PROP_NAMES}
                    />
                </Stack>
            }
        >
            {leftProperties.map((prop, idx) => (
                <Line
                    key={prop}
                    type="monotone"
                    dataKey={prop}
                    yAxisId="left"
                    stroke={COLORS[idx % COLORS.length]}
                    fill={COLORS[idx % COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                />
            ))}
            {rightProperties.map((prop, idx) => (
                <Line
                    key={prop}
                    type="monotone"
                    dataKey={prop}
                    yAxisId="right"
                    stroke={COLORS[(idx + leftProperties.length) % COLORS.length]}
                    fill={COLORS[(idx + leftProperties.length) % COLORS.length]}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                />
            ))}
            <YAxis
                yAxisId="right"
                domain={["auto", "auto"]}
                fontSize={14}
                label={{
                    angle: 90,
                    dx: 20,
                    dy: 0,
                    textAnchor: "middle",
                }}
                orientation="right"
            />
        </BaseChart>
    );
};

const mapFieldResultsToChartData = (r: FieldResult): DataProps => ({
    date: dayjs(r.date).toDate(),
    ..._.omit(r.aggregatedProps, "date"),
});

export type DataProps = TimeSeriesDataProps & Omit<FieldResultAggregatedProps, "date">;

interface PropertySearchProps {
    placeholder: string;
    properties: (keyof FieldResultAggregatedProps)[];
    onChange: (value: (keyof FieldResultAggregatedProps)[]) => void;
    options: string[];
}

const PropertySearch: React.FC<PropertySearchProps> = ({placeholder, properties, onChange, options}) => {
    return (
        <Autocomplete
            multiple
            fullWidth
            options={options as unknown as (keyof FieldResultAggregatedProps)[]}
            autoComplete={true}
            autoHighlight={true}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="filled"
                    hiddenLabel
                    margin="none"
                    size="small"
                    type="text"
                    placeholder={placeholder}
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                    }}
                />
            )}
            value={properties}
            onChange={(_, newValue) => {
                onChange(newValue);
            }}
        />
    );
};

// List of simple, distinct colors for dynamically constructed chart
const COLORS = [
    "#E6194B",
    "#3CB44B",
    "#4363D8",
    "#F58231",
    "#911EB4",
    "#46F0F0",
    "#F032E6",
    "#BCF60C",
    "#FABEBE",
    "#008080",
    "#E6BEFF",
    "#9A6324",
    "#FFFAC8",
    "#800000",
    "#AAFFC3",
    "#808000",
    "#FFD8B1",
    "#000075",
    "#808080",
    "#FFE119",
];
